<script>
  import { onDestroy, onMount, tick } from "svelte";
  import Header from "lib/components/Header.svelte";
  import { Accordion, SectionWrapper, Tooltip } from "paxray-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { formatTimeDelta } from "lib/util/formatfunctions.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import CaseIdSelection from "./CaseIdSelection.svelte";
  import CaseIdGraphPreview from "./CaseIdGraphPreview.svelte";
  import CaseIdBpmnExportModal from "./CaseIdBpmnExportModal.svelte";
  import BpmnViewer from "./BpmnViewer.svelte";
  import {
    Loading,
    Modal as DangerModal,
    OverflowMenu,
    OverflowMenuItem,
    ToastNotification,
  } from "carbon-components-svelte";
  import ButtonPlusMinus from "lib/components/buttonPlusMinus.svelte";
  import ButtonIconOnly from "lib/components/buttonIconOnly.svelte";
  import AccordionPagination from "lib/components/accordionPagination.svelte";
  import Filter from "../analysis/components/filter/Filter.svelte";

  const urlParams = useUrlParams();

  $: ({ idproject } = $urlParams);

  let fullscreen = false;
  let caseids = [];
  let caseIdsLoading = true;
  let selectedCaseids = [];
  let selectedStartnodes = [];
  let graphData = [];
  let loadingGraph = false;
  let updatingGraph = false;
  let graphRequestId = 0;
  let graphRevision = 0;
  let loadedCaseIdsProject = "";
  let lastGraphSelectionKey = "";
  let graphPreview;
  let bpmnViewerElement;
  let minFrequency = 1;
  let playingVariantId = "";
  let playbackNode = "";
  let playbackFrom = "";
  let playbackTo = "";
  let playbackDuration = null;
  let playbackTimer;
  let selectedVariantIds = [];
  let edgeMetric = 0;
  let actionMenuOpen = false;
  let bpmnModalOpen = false;
  let bpmnExportLevel = "CASE";
  let generatedBpmnXml = "";
  let generatedBpmnFrameworkPath = [];
  let generatedBpmnName = "";
  let generatedBpmnActivityMetrics = [];
  let showBpmnViewer = false;
  let bpmnExportFormat = "standard";
  let bpmnOrientation = "horizontal";
  let bicIdentifier = "";
  let bicValidTo = "";
  let bicChangeLog = "";
  let bicGenerateTexts = false;
  let bpmnSaving = false;
  let bpmnSaved = false;
  let bpmnSaveNotice = null;
  let graphMode = "transitions";
  let colorMetric = "none";
  let appNameMappings = [];
  let appTypesByName = {};
  let legendOpen = true;
  let quantificationOpen = true;
  let applicationsOpen = true;
  let zoomLevel = 100;
  let selectedTransitionIds = [];
  let transitionPage = 1;
  const transitionsPerPage = 5;
  let paxbotOpen = false;
  let paxbotInput = "";
  let paxbotLoading = false;
  let paxbotMessages = [
    {
      role: "assistant",
      text: "Hi, I'm Pax AI. Ask me anything about the current object graph. I can also help with navigation or interpretation questions.",
    },
  ];
  let paxbotLanguage = "en";
  let variantPage = 1;
  const variantsPerPage = 5;
  let savedBpmnDrawings = [];
  let bpmnDrawingsLoadedProject = "";
  let selectedBpmnDrawingId = "";
  let selectedBpmnDrawingStartnode = "";
  let activeBpmnDrawingGraph = null;
  let pendingBpmnDrawingReference = null;
  let deleteBpmnDrawingOpen = false;
  let bpmnDrawingToDelete = null;
  const TRAFFIC_LIGHT_LEGEND = [
    { label: "High", color: "#e6a7ad" },
    { label: "Medium", color: "#e5e8a8" },
    { label: "Low", color: "#b7e3bd" },
  ];
  const APP_TYPE_DEFINITIONS = [
    { key: "main", label: "Main Apps", color: "#b7e3bd" },
    { key: "support", label: "Support Apps", color: "#e5e8a8" },
    { key: "communication", label: "Communication Apps", color: "#abcfe8" },
    { key: "unwanted", label: "Unwanted Apps", color: "#e6a7ad" },
  ];
  const paxbotLanguages = [
    { key: "en", label: "EN", name: "English" },
    { key: "de", label: "DE", name: "German" },
    { key: "es", label: "ES", name: "Spanish" },
    { key: "fr", label: "FR", name: "French" },
    { key: "it", label: "IT", name: "Italian" },
  ];

  function groupCaseIdRules(rules) {
    const groups = new Map();
    (Array.isArray(rules) ? rules : []).forEach((rule) => {
      const key = String(rule.record_type || "")
        .trim()
        .toLowerCase();
      const ranges =
        Array.isArray(rule.ranges) && rule.ranges.length
          ? rule.ranges
          : [{ prefix: rule.prefix, length: rule.length }];
      if (!groups.has(key)) {
        groups.set(key, {
          ...rule,
          ranges: [...ranges],
          case_count: Number(rule.case_count || 0),
          startnodes: [...new Set(rule.startnodes || [])],
        });
        return;
      }
      const group = groups.get(key);
      group.ranges.push(...ranges);
      group.case_count += Number(rule.case_count || 0);
      group.startnodes = [
        ...new Set([...group.startnodes, ...(rule.startnodes || [])]),
      ];
      const lengths = [...new Set(group.ranges.map((item) => item.length))];
      group.length = lengths.length === 1 ? lengths[0] : lengths.join(", ");
    });
    return [...groups.values()];
  }

  function calculateKpis(variants, allVariants, nodes) {
    const totalCases = allVariants.reduce(
      (sum, variant) => sum + Number(variant.count || 0),
      0,
    );
    const cases = variants.reduce(
      (sum, variant) => sum + Number(variant.count || 0),
      0,
    );
    const duration = variants.reduce(
      (sum, variant) =>
        sum +
        Number(variant.averageDurationSeconds || 0) *
          Number(variant.count || 0),
      0,
    );
    const nodeMetrics = new Map(
      nodes.map((node) => [node.id, node.metrics || {}]),
    );
    const appWeights = new Map();
    variants.forEach((variant) => {
      (variant.path || []).forEach((activity) => {
        Object.entries(nodeMetrics.get(activity)?.apps || {}).forEach(
          ([app, share]) => {
            appWeights.set(
              app,
              (appWeights.get(app) || 0) +
                Number(share || 0) * Number(variant.count || 0),
            );
          },
        );
      });
    });
    const totalAppWeight = [...appWeights.values()].reduce(
      (sum, value) => sum + value,
      0,
    );
    const applications = [...appWeights.entries()]
      .map(([name, value]) => ({
        name,
        share: totalAppWeight ? value / totalAppWeight : 0,
      }))
      .sort((a, b) => b.share - a.share)
      .slice(0, 5);
    return {
      cases,
      coverage: totalCases ? cases / totalCases : 0,
      averageDurationSeconds: cases ? duration / cases : 0,
      applications,
    };
  }

  function toggleVariant(id) {
    const nextSelection = selectedVariantIds.includes(id)
      ? selectedVariantIds.filter((variantId) => variantId !== id)
      : [...selectedVariantIds, id];
    selectedVariantIds = nextSelection;
    if (!selectedVariantIds.includes(playingVariantId)) stopVariantPlayback();
    getGraphData(nextSelection);
  }

  function selectVariants(ids) {
    selectedVariantIds = ids;
    stopVariantPlayback();
    getGraphData(ids);
  }

  $: graphNodes = Array.isArray(activeBpmnDrawingGraph?.nodes)
    ? activeBpmnDrawingGraph.nodes
    : Array.isArray(graphData?.nodes)
      ? graphData.nodes
      : [];
  $: rawGraphEdges = Array.isArray(activeBpmnDrawingGraph?.edges)
    ? activeBpmnDrawingGraph.edges
    : Array.isArray(graphData?.edges)
      ? graphData.edges
      : [];
  $: caseVariants = Array.isArray(graphData?.variants)
    ? graphData.variants
    : [];
  $: selectedVariants = caseVariants.filter((variant) =>
    selectedVariantIds.includes(variant.id),
  );
  $: variantPageCount = Math.max(
    1,
    Math.ceil(caseVariants.length / variantsPerPage),
  );
  $: if (variantPage > variantPageCount) variantPage = variantPageCount;
  $: displayedVariants = caseVariants.slice(
    (variantPage - 1) * variantsPerPage,
    variantPage * variantsPerPage,
  );
  $: transitionOptions = [...rawGraphEdges].sort(
    (a, b) => Number(b.value || 0) - Number(a.value || 0),
  );
  $: transitionPageCount = Math.max(
    1,
    Math.ceil(transitionOptions.length / transitionsPerPage),
  );
  $: if (transitionPage > transitionPageCount)
    transitionPage = transitionPageCount;
  $: displayedTransitions = transitionOptions.slice(
    (transitionPage - 1) * transitionsPerPage,
    transitionPage * transitionsPerPage,
  );
  $: graphEdges =
    graphMode === "transitions"
      ? rawGraphEdges.filter((edge) =>
          selectedTransitionIds.includes(transitionId(edge)),
        )
      : rawGraphEdges;
  $: selectedTransitionActivities = [
    ...new Set(
      graphEdges
        .flatMap((edge) => [edge.from, edge.to])
        .filter((id) => id !== "Start" && id !== "End"),
    ),
  ];
  $: selectionKpis = activeBpmnDrawingGraph
    ? calculateDrawingKpis(graphNodes, graphEdges)
    : calculateKpis(selectedVariants, caseVariants, graphNodes);
  $: bpmnTaskName =
    caseids
      .filter((item) => selectedCaseids.includes(item.id))
      .map((item) => item.record_type)
      .filter(Boolean)
      .join(", ") || "Object Process";
  $: selectedVariantStats = graphData?.variantStats || {};
  $: buildAppTypeMapping(appNameMappings, selectionKpis.applications);
  $: legendItems =
    colorMetric === "appType"
      ? APP_TYPE_DEFINITIONS
      : colorMetric === "none"
        ? []
        : TRAFFIC_LIGHT_LEGEND;
  $: manualDrawingOptions = savedBpmnDrawings.map((drawing) => ({
    id: drawing.id,
    name: drawing.name,
    startnodes: getBpmnDrawingStartnodes(drawing),
  }));

  function transitionId(edge) {
    return `${edge.from}\u0000${edge.to}`;
  }

  function calculateDrawingKpis(nodes, edges) {
    const drawingEdgeIds = new Set(edges.map(transitionId));
    const matchingVariants = caseVariants.filter((variant) => {
      const collapsedPath = (variant.path || []).filter(
        (activity, index, path) => index === 0 || activity !== path[index - 1],
      );
      const sequence = ["Start", ...collapsedPath, "End"];
      return sequence
        .slice(1)
        .every((target, index) =>
          drawingEdgeIds.has(
            transitionId({ from: sequence[index], to: target }),
          ),
        );
    });
    const totalKnownCases = caseVariants.reduce(
      (sum, variant) => sum + Number(variant.count || 0),
      0,
    );
    const matchingCases = matchingVariants.reduce(
      (sum, variant) => sum + Number(variant.count || 0),
      0,
    );
    const matchingDuration = matchingVariants.reduce(
      (sum, variant) =>
        sum +
        Number(variant.averageDurationSeconds || 0) *
          Number(variant.count || 0),
      0,
    );
    const startValues = edges
      .filter(
        (edge) =>
          edge.from === "Start" &&
          edge.value !== null &&
          edge.value !== undefined &&
          edge.value !== "" &&
          Number.isFinite(Number(edge.value)),
      )
      .map((edge) => Number(edge.value));
    const applications = new Map();
    nodes.forEach((node) =>
      Object.entries(node.metrics?.apps || {}).forEach(([name, share]) =>
        applications.set(
          name,
          (applications.get(name) || 0) + Number(share || 0),
        ),
      ),
    );
    const totalApps = [...applications.values()].reduce(
      (sum, value) => sum + value,
      0,
    );
    return {
      cases:
        matchingCases ||
        (startValues.length
          ? startValues.reduce((sum, value) => sum + value, 0)
          : null),
      averageDurationSeconds: matchingCases
        ? matchingDuration / matchingCases
        : null,
      coverage:
        matchingCases && totalKnownCases
          ? matchingCases / totalKnownCases
          : null,
      applications: [...applications]
        .map(([name, value]) => ({
          name,
          share: totalApps ? value / totalApps : 0,
        }))
        .sort((a, b) => b.share - a.share),
    };
  }

  function normalizeAppName(name) {
    return `${name || ""}`.trim().toLowerCase();
  }

  function normalizeAppType(type) {
    const normalized = `${type || ""}`.trim().toLowerCase();
    return normalized === "supportive" ? "support" : normalized;
  }

  function getAppTypeCandidates(item) {
    return [
      item.id,
      item.applicationname,
      item.appname,
      item.name,
      item.clearname,
      item.windowtitle,
    ];
  }

  function buildAppTypeMapping(mappings, apps = []) {
    const next = {};
    mappings.forEach((mapping) => {
      const type = normalizeAppType(mapping.apptype);
      if (!type) return;
      getAppTypeCandidates(mapping).forEach((name) => {
        const key = normalizeAppName(name);
        if (key) next[key] = type;
      });
    });
    apps.forEach((app) => {
      const type = getAppTypeCandidates(app)
        .map((name) => next[normalizeAppName(name)])
        .find(Boolean);
      if (!type) return;
      getAppTypeCandidates(app).forEach((name) => {
        const key = normalizeAppName(name);
        if (key) next[key] = type;
      });
    });
    appTypesByName = next;
  }

  function loadAppnameMapping() {
    fetchWithSession(`/api/damprojects/getAppnameMapping`)
      .then((res) => res.json())
      .then((data) => (appNameMappings = data || []))
      .catch(() => (appNameMappings = []));
  }

  function bpmnDrawingsStorageKey() {
    return `paxray.caseid.bpmn.drawings.${idproject}`;
  }

  function loadSavedBpmnDrawings() {
    if (!idproject || typeof localStorage === "undefined") return;
    try {
      savedBpmnDrawings = JSON.parse(
        localStorage.getItem(bpmnDrawingsStorageKey()) || "[]",
      );
    } catch {
      savedBpmnDrawings = [];
    }
    bpmnDrawingsLoadedProject = String(idproject);
  }

  function persistBpmnDrawings() {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(
      bpmnDrawingsStorageKey(),
      JSON.stringify(savedBpmnDrawings),
    );
  }

  function saveBpmnDrawing(event) {
    const baseName =
      String(event.detail.name || "BPMN Drawing").trim() || "BPMN Drawing";
    const usedNames = new Set(
      savedBpmnDrawings.map((drawing) => drawing.name.toLowerCase()),
    );
    let name = baseName;
    let index = 2;
    while (usedNames.has(name.toLowerCase())) name = `${baseName} (${index++})`;
    const now = new Date().toISOString();
    const drawing = {
      id: now,
      projectId: idproject,
      name,
      xml: event.detail.xml,
      graph: event.detail.graph || {
        nodes: graphData?.nodes || [],
        edges: graphData?.edges || [],
      },
      createdAt: now,
      updatedAt: now,
    };
    savedBpmnDrawings = [drawing, ...savedBpmnDrawings];
    selectedBpmnDrawingId = drawing.id;
    persistBpmnDrawings();
  }

  async function loadBpmnDrawing(drawing, startnode = "") {
    const requestId = ++graphRequestId;
    const drawingStartnodes = startnode
      ? [startnode]
      : getBpmnDrawingStartnodes(drawing);
    activeBpmnDrawingGraph = filterBpmnDrawingByStartnode(
      buildBpmnDrawingGraph(drawing),
      startnode,
    );
    selectedBpmnDrawingId = drawing.id;
    selectedBpmnDrawingStartnode = startnode;
    selectedCaseids = [];
    selectedStartnodes = [];
    lastGraphSelectionKey = "";
    bpmnModalOpen = false;
    showBpmnViewer = false;
    graphMode = "transitions";
    minFrequency = 1;
    selectedTransitionIds = activeBpmnDrawingGraph.edges.map(transitionId);
    graphRevision += 1;

    const matchingCaseids = caseids.filter((item) =>
      (item.startnodes || []).some((node) => drawingStartnodes.includes(node)),
    );
    const referenceCaseids = matchingCaseids.length ? matchingCaseids : caseids;
    const ranges = referenceCaseids.flatMap(
      (item) => item.ranges || [{ prefix: item.prefix, length: item.length }],
    );
    if (!ranges.length) {
      pendingBpmnDrawingReference = { drawing, startnode };
      return;
    }
    pendingBpmnDrawingReference = null;

    updatingGraph = true;
    const params = new URLSearchParams({
      caseids: JSON.stringify(ranges),
      startnodes: JSON.stringify(drawingStartnodes),
    });
    try {
      const response = await fetchWithSession(
        `/api/damprojects/case-id-graphs/${idproject}?${params.toString()}`,
        {
          method: "GET",
        },
      );
      if (!response.ok)
        throw new Error(`Reference graph request failed (${response.status})`);
      const data = await response.json();
      if (requestId !== graphRequestId) return;
      graphData = data || { nodes: [], edges: [], variants: [] };
      activeBpmnDrawingGraph = filterBpmnDrawingByStartnode(
        buildBpmnDrawingGraph(drawing),
        startnode,
      );
      selectedTransitionIds = activeBpmnDrawingGraph.edges.map(transitionId);
      selectedVariantIds = (graphData.variants || []).map(
        (variant) => variant.id,
      );
      transitionPage = 1;
      variantPage = 1;
      graphRevision += 1;
    } catch (error) {
      if (requestId === graphRequestId)
        console.error("Error loading BPMN reference data:", error);
    } finally {
      if (requestId === graphRequestId) updatingGraph = false;
    }
  }

  async function saveBpmnToFramework(event) {
    if (bpmnSaving) return;
    if (
      !generatedBpmnFrameworkPath.length ||
      generatedBpmnFrameworkPath.length > 3
    ) {
      bpmnSaveNotice = {
        kind: "error",
        title: "BPMN could not be saved",
        message: "The visible tasks have no common L1–L3 framework assignment.",
      };
      return;
    }

    bpmnSaving = true;
    bpmnSaved = false;
    try {
      const response = await fetchWithSession(
        "/api/pman/saveFrameworkProcessModel",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            levels: generatedBpmnFrameworkPath,
            name: generatedBpmnName || bpmnTaskName || "Generated Process",
            bpmnstring: event.detail.xml,
            activity_metrics: generatedBpmnActivityMetrics,
          }),
        },
      );
      const result = await response.json();
      if (!response.ok || result.status !== "success") {
        throw new Error(result.message || "Saving the BPMN model failed.");
      }
      bpmnSaved = true;
      bpmnSaveNotice = {
        kind: "success",
        title: "BPMN saved",
        message: `${result.process.name} was saved under ${generatedBpmnFrameworkPath.join(" / ")}.`,
      };
    } catch (error) {
      bpmnSaveNotice = {
        kind: "error",
        title: "BPMN could not be saved",
        message: error.message || "Saving the BPMN model failed.",
      };
    } finally {
      bpmnSaving = false;
    }
  }

  function buildBpmnDrawingGraph(drawing) {
    const xml = new DOMParser().parseFromString(drawing.xml, "application/xml");
    const elements = [...xml.querySelectorAll("*")];
    const topLevel = (element) => {
      let parent = element.parentElement;
      while (parent && parent.localName !== "process") {
        if (parent.localName === "subProcess") return false;
        parent = parent.parentElement;
      }
      return parent?.localName === "process";
    };
    const activityTypes = new Set([
      "task",
      "userTask",
      "serviceTask",
      "scriptTask",
      "sendTask",
      "receiveTask",
      "manualTask",
      "businessRuleTask",
      "subProcess",
      "startEvent",
      "endEvent",
    ]);
    const activities = elements.filter(
      (element) => activityTypes.has(element.localName) && topLevel(element),
    );
    const activityById = new Map(
      activities.map((element) => {
        const id = element.getAttribute("id");
        const displayId =
          element.localName === "startEvent"
            ? "Start"
            : element.localName === "endEvent"
              ? "End"
              : element.getAttribute("name") || id;
        return [id, { id: displayId, label: displayId, sourceId: id }];
      }),
    );
    const flows = elements.filter(
      (element) => element.localName === "sequenceFlow" && topLevel(element),
    );
    const outgoing = new Map();
    flows.forEach((flow) => {
      const source = flow.getAttribute("sourceRef");
      if (!outgoing.has(source)) outgoing.set(source, []);
      outgoing.get(source).push(flow.getAttribute("targetRef"));
    });
    const edgePairs = new Map();
    function findNextActivities(nodeId, visited = new Set()) {
      if (visited.has(nodeId)) return [];
      if (activityById.has(nodeId)) return [activityById.get(nodeId).id];
      const nextVisited = new Set([...visited, nodeId]);
      return [
        ...new Set(
          (outgoing.get(nodeId) || []).flatMap((target) =>
            findNextActivities(target, nextVisited),
          ),
        ),
      ];
    }
    activityById.forEach((source, sourceId) => {
      if (source.id === "End") return;
      (outgoing.get(sourceId) || [])
        .flatMap((target) => findNextActivities(target, new Set([sourceId])))
        .forEach((target) => {
          if (target !== source.id)
            edgePairs.set(`${source.id}\u0000${target}`, {
              from: source.id,
              to: target,
            });
        });
    });
    const drawingNodes = Array.isArray(drawing.graph?.nodes)
      ? drawing.graph.nodes
      : [];
    const knownNodes = Array.isArray(graphData?.nodes) ? graphData.nodes : [];
    const sourceNodes = drawingNodes.length ? drawingNodes : knownNodes;
    const drawingEdges = Array.isArray(drawing.graph?.edges)
      ? drawing.graph.edges
      : [];
    const knownEdges = Array.isArray(graphData?.edges) ? graphData.edges : [];
    const sourceEdges = drawingEdges.length ? drawingEdges : knownEdges;
    const laneByNode = new Map();
    const drawingLanes = new Map();
    elements
      .filter((element) => element.localName === "lane")
      .forEach((lane, index) => {
        const laneId = `drawing-lane-${index}`;
        drawingLanes.set(laneId, {
          id: laneId,
          label: lane.getAttribute("name") || "Unassigned",
        });
        [...lane.children]
          .filter((child) => child.localName === "flowNodeRef")
          .forEach((reference) =>
            laneByNode.set(reference.textContent.trim(), laneId),
          );
      });
    const resultNodes = new Map();
    activityById.forEach((activity) => {
      const existing = sourceNodes.find(
        (node) => node.id === activity.id || node.label === activity.label,
      );
      const known = knownNodes.find(
        (node) => node.id === activity.id || node.label === activity.label,
      );
      resultNodes.set(activity.id, {
        ...(known || {}),
        ...(existing || {}),
        id: activity.id,
        label: activity.label,
        parentNode: ["Start", "End"].includes(activity.id)
          ? undefined
          : existing?.parentNode ||
            known?.parentNode ||
            laneByNode.get(activity.sourceId),
        metrics: Object.keys(existing?.metrics || {}).length
          ? existing.metrics
          : known?.metrics || {},
        caseIdActivities: existing?.caseIdActivities?.length
          ? existing.caseIdActivities
          : known?.caseIdActivities || [],
      });
    });
    const referencedParents = new Set(
      [...resultNodes.values()].map((node) => node.parentNode).filter(Boolean),
    );
    sourceNodes
      .filter((node) => referencedParents.has(node.id))
      .forEach((node) => resultNodes.set(node.id, node));
    drawingLanes.forEach((lane, laneId) => {
      if (referencedParents.has(laneId)) resultNodes.set(laneId, lane);
    });
    const resultEdges = [...edgePairs.values()].map((edge) => {
      const existing = sourceEdges.find(
        (item) => item.from === edge.from && item.to === edge.to,
      );
      const known = knownEdges.find(
        (item) => item.from === edge.from && item.to === edge.to,
      );
      const valueSource = [existing, known].find(
        (item) =>
          item?.value !== null &&
          item?.value !== undefined &&
          item?.value !== "" &&
          Number.isFinite(Number(item.value)),
      );
      const durationSource = [existing, known].find(
        (item) => Number(item?.averageDurationSeconds) > 0,
      );
      return {
        ...edge,
        value: valueSource ? Number(valueSource.value) : null,
        averageDurationSeconds: durationSource
          ? Number(durationSource.averageDurationSeconds)
          : null,
      };
    });
    return { nodes: [...resultNodes.values()], edges: resultEdges };
  }

  function getBpmnDrawingStartnodes(drawing) {
    if (typeof DOMParser === "undefined") return [];
    try {
      const edges = buildBpmnDrawingGraph(drawing).edges;
      return [
        ...new Set(
          edges
            .filter((edge) => edge.from === "Start")
            .map((edge) => edge.to)
            .filter(Boolean),
        ),
      ];
    } catch {
      return [];
    }
  }

  function filterBpmnDrawingByStartnode(graph, startnode) {
    if (!startnode) return graph;
    const edges = Array.isArray(graph.edges) ? graph.edges : [];
    const selectedEdges = [];
    const reachableNodes = new Set(["Start", startnode]);
    const processedNodes = new Set();
    const queue = [startnode];
    const startEdge = edges.find(
      (edge) => edge.from === "Start" && edge.to === startnode,
    );
    if (startEdge) selectedEdges.push(startEdge);

    while (queue.length) {
      const source = queue.shift();
      if (processedNodes.has(source)) continue;
      processedNodes.add(source);
      edges
        .filter((edge) => edge.from === source)
        .forEach((edge) => {
          selectedEdges.push(edge);
          reachableNodes.add(edge.to);
          if (!processedNodes.has(edge.to)) queue.push(edge.to);
        });
    }

    const parentNodes = new Set(
      graph.nodes
        .filter((node) => reachableNodes.has(node.id))
        .map((node) => node.parentNode)
        .filter(Boolean),
    );
    return {
      ...graph,
      nodes: graph.nodes.filter(
        (node) => reachableNodes.has(node.id) || parentNodes.has(node.id),
      ),
      edges: [
        ...new Map(
          selectedEdges.map((edge) => [transitionId(edge), edge]),
        ).values(),
      ],
    };
  }

  function renameBpmnDrawing(id, name) {
    savedBpmnDrawings = savedBpmnDrawings.map((item) =>
      item.id === id
        ? { ...item, name, updatedAt: new Date().toISOString() }
        : item,
    );
    persistBpmnDrawings();
  }

  function openDeleteBpmnDrawing(drawing) {
    bpmnDrawingToDelete = drawing;
    deleteBpmnDrawingOpen = true;
  }

  function confirmDeleteBpmnDrawing() {
    if (!bpmnDrawingToDelete) return;
    savedBpmnDrawings = savedBpmnDrawings.filter(
      (drawing) => drawing.id !== bpmnDrawingToDelete.id,
    );
    if (selectedBpmnDrawingId === bpmnDrawingToDelete.id) {
      selectedBpmnDrawingId = "";
      selectedBpmnDrawingStartnode = "";
      activeBpmnDrawingGraph = null;
      selectedTransitionIds = (graphData?.edges || []).map(transitionId);
      graphRevision += 1;
    }
    persistBpmnDrawings();
    deleteBpmnDrawingOpen = false;
    bpmnDrawingToDelete = null;
  }

  // Zeitangaben wie im Process Explorer: "05:12 min" (Minuten:Sekunden), nicht
  // "05min 12s" - das lief in der Kachel ueber und war nicht lesbar.
  function formatTransitionDuration(value) {
    return Number(value) > 0 ? formatTimeDelta(Number(value)) : "-";
  }

  function formatOptionalTime(value) {
    if (value === null || value === undefined || value === "") return "-";
    const seconds = Number(value);
    return Number.isFinite(seconds) && seconds >= 0
      ? formatTimeDelta(seconds)
      : "-";
  }

  async function toggleFullscreen() {
    fullscreen = !fullscreen;
    await tick();
    if (showBpmnViewer) bpmnViewerElement?.resetZoom();
    else graphPreview?.centerGraph();
  }

  function refreshGraphLayout() {
    graphRevision += 1;
  }

  function setGraphMode(mode) {
    if (graphMode === mode) return;
    graphMode = mode;
    refreshGraphLayout();
  }

  function toggleTransition(edge) {
    const id = transitionId(edge);
    selectedTransitionIds = selectedTransitionIds.includes(id)
      ? selectedTransitionIds.filter((item) => item !== id)
      : [...selectedTransitionIds, id];
    refreshGraphLayout();
  }

  function selectAllTransitions() {
    selectedTransitionIds = rawGraphEdges.map(transitionId);
    refreshGraphLayout();
  }

  function clearTransitions() {
    selectedTransitionIds = [];
    refreshGraphLayout();
  }

  function addNextTransition() {
    const edge = transitionOptions.find(
      (item) => !selectedTransitionIds.includes(transitionId(item)),
    );
    if (edge) toggleTransition(edge);
    return selectedTransitionIds.length;
  }

  function removeLastTransition() {
    const edge = [...transitionOptions]
      .reverse()
      .find((item) => selectedTransitionIds.includes(transitionId(item)));
    if (edge) toggleTransition(edge);
    return selectedTransitionIds.length;
  }

  function addNextActivity() {
    const selected = new Set(selectedTransitionActivities);
    const activity = graphNodes.find(
      (node) => node.parentNode && !selected.has(node.id),
    );
    if (activity) {
      selectedTransitionIds = [
        ...new Set([
          ...selectedTransitionIds,
          ...rawGraphEdges
            .filter(
              (edge) => edge.from === activity.id || edge.to === activity.id,
            )
            .map(transitionId),
        ]),
      ];
      refreshGraphLayout();
    }
    return selectedTransitionActivities.length;
  }

  function removeLastActivity() {
    const activity =
      selectedTransitionActivities[selectedTransitionActivities.length - 1];
    if (activity) {
      selectedTransitionIds = selectedTransitionIds.filter((id) => {
        const edge = rawGraphEdges.find((item) => transitionId(item) === id);
        return edge && edge.from !== activity && edge.to !== activity;
      });
      refreshGraphLayout();
    }
    return selectedTransitionActivities.length;
  }

  function stopVariantPlayback() {
    if (playbackTimer) clearTimeout(playbackTimer);
    playbackTimer = null;
    playingVariantId = "";
    playbackNode = "";
    playbackFrom = "";
    playbackTo = "";
    playbackDuration = null;
  }

  function playVariant(variant) {
    if (playingVariantId === variant.id) {
      stopVariantPlayback();
      return;
    }
    stopVariantPlayback();
    const path = ["Start", ...(variant.path || []), "End"];
    playingVariantId = variant.id;
    let index = 0;

    function showStep() {
      if (playingVariantId !== variant.id) return;
      playbackNode = path[index];
      playbackFrom = index > 0 ? path[index - 1] : "";
      playbackTo = index > 0 ? path[index] : "";
      playbackDuration =
        index > 0
          ? (variant.averageStepDurationSeconds?.[index - 1] ?? null)
          : null;
      playbackTimer = setTimeout(() => {
        if (index >= path.length - 1) {
          stopVariantPlayback();
          return;
        }
        if (path[index + 1] === path[index]) {
          playbackNode = "";
          playbackFrom = "";
          playbackTo = "";
          playbackDuration = null;
          playbackTimer = setTimeout(() => {
            index += 1;
            showStep();
          }, 220);
          return;
        }
        index += 1;
        showStep();
      }, 900);
    }

    showStep();
  }

  function getPaxbotContext() {
    return {
      page: "Object Explorer",
      projectId: idproject,
      navigation: {
        graphMode,
        edgeMetric: edgeMetric === 0 ? "count" : "average throughput time",
        colorMetric,
        hint: "Use Object Selection to choose an object type and start activity. Switch between transitions and variants in the top-left toolbar. Expand nodes with + for object matches and v for activity KPIs.",
      },
      responseLanguage:
        paxbotLanguages.find((language) => language.key === paxbotLanguage)
          ?.name || "English",
      selectedRecordTypes: caseids
        .filter((item) => selectedCaseids.includes(item.id))
        .map((item) => ({
          name: item.record_type,
          cases: item.case_count,
          startNodes: item.startnodes,
        })),
      selectedStartNodes: selectedStartnodes,
      quantification: selectionKpis,
      variants: caseVariants.map((variant) => ({
        id: variant.id,
        name: variant.name,
        selected: selectedVariantIds.includes(variant.id),
        cases: variant.count,
        coverage: variant.coverage,
        averageDurationSeconds: variant.averageDurationSeconds,
        path: variant.path,
      })),
      transitions: rawGraphEdges.map((edge) => ({
        from: edge.from,
        to: edge.to,
        selected: selectedTransitionIds.includes(transitionId(edge)),
        count: edge.value,
        averageDurationSeconds: edge.averageDurationSeconds,
      })),
      activities: graphNodes
        .filter((node) => node.parentNode)
        .map((node) => ({
          name: node.label,
          metrics: node.metrics,
          caseIdHits: node.caseIdActivities,
        })),
    };
  }

  function resetPaxbotChat() {
    paxbotMessages = [
      {
        role: "assistant",
        text: "Hi, I'm Pax AI. Ask me anything about the current object graph. I can also help with navigation or interpretation questions.",
      },
    ];
    paxbotInput = "";
  }

  async function askPaxbot(question = paxbotInput) {
    const text = question.trim();
    if (!text || paxbotLoading) return;
    const history = paxbotMessages;
    paxbotMessages = [...paxbotMessages, { role: "user", text }];
    paxbotInput = "";
    paxbotLoading = true;
    try {
      const response = await fetchWithSession(`/api/damprojects/paxbot/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: text,
          history,
          context: getPaxbotContext(),
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status === "error")
        throw new Error(result.message || "Paxbot request failed");
      paxbotMessages = [
        ...paxbotMessages,
        { role: "assistant", text: result.answer },
      ];
    } catch (error) {
      paxbotMessages = [
        ...paxbotMessages,
        {
          role: "assistant",
          text: `Paxbot could not answer: ${error.message}`,
        },
      ];
    } finally {
      paxbotLoading = false;
    }
  }

  function getData() {
    const params = new URLSearchParams({ project_id: String(idproject) });
    caseIdsLoading = true;
    fetchWithSession(`/api/damprojects/number-range-graphs/${idproject}`, {
      method: "GET",
    })
      .then((res) => res.json())
      .then((data) => {
        caseids = groupCaseIdRules(data).sort((a, b) => {
          if (a.record_type.toLowerCase() < b.record_type.toLowerCase())
            return -1;
          if (a.record_type.toLowerCase() > b.record_type.toLowerCase())
            return 1;
          return 0;
        });
        if (
          caseids.length &&
          selectedCaseids.length === 0 &&
          !activeBpmnDrawingGraph
        ) {
          selectedCaseids = [caseids[0].id];
          selectedStartnodes = caseids[0].startnodes?.length
            ? [caseids[0].startnodes[0]]
            : [];
        }
        caseIdsLoading = false;
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        caseIdsLoading = false;
      });
  }

  function getGraphData(variantIds = null) {
    stopVariantPlayback();
    pendingBpmnDrawingReference = null;
    activeBpmnDrawingGraph = null;
    selectedBpmnDrawingId = "";
    selectedBpmnDrawingStartnode = "";
    const requestId = ++graphRequestId;
    const initialLoad = variantIds === null;
    loadingGraph = initialLoad;
    updatingGraph = !initialLoad;
    const params = new URLSearchParams({
      caseids: JSON.stringify(
        caseids
          .filter((item) => selectedCaseids.includes(item.id))
          .flatMap(
            (item) =>
              item.ranges || [{ prefix: item.prefix, length: item.length }],
          ),
      ),
      startnodes: JSON.stringify(selectedStartnodes),
    });
    if (!initialLoad)
      params.set("selectedvariants", JSON.stringify(variantIds));
    fetchWithSession(
      `/api/damprojects/case-id-graphs/${idproject}?${params.toString()}`,
      {
        method: "GET",
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (requestId !== graphRequestId) return;
        graphData = data || { nodes: [], edges: [] };
        selectedTransitionIds = (graphData.edges || []).map(transitionId);
        graphRevision += 1;
        if (initialLoad) {
          selectedVariantIds = (graphData.variants || []).map(
            (variant) => variant.id,
          );
          variantPage = 1;
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      })
      .finally(() => {
        if (requestId !== graphRequestId) return;
        loadingGraph = false;
        updatingGraph = false;
      });
  }

  $: if (idproject && String(idproject) !== loadedCaseIdsProject) {
    loadedCaseIdsProject = String(idproject);
    selectedCaseids = [];
    selectedStartnodes = [];
    graphData = { nodes: [], edges: [] };
    lastGraphSelectionKey = "";
    getData();
  }

  $: graphSelectionKey = JSON.stringify({
    project: idproject,
    caseids: [...selectedCaseids].sort(),
    startnodes: [...selectedStartnodes].sort(),
  });
  $: if (
    selectedCaseids.length > 0 &&
    graphSelectionKey !== lastGraphSelectionKey
  ) {
    lastGraphSelectionKey = graphSelectionKey;
    getGraphData();
  }

  $: if (selectedCaseids.length === 0 && !activeBpmnDrawingGraph) {
    graphData = { nodes: [], edges: [] };
    selectedVariantIds = [];
    selectedTransitionIds = [];
    transitionPage = 1;
    variantPage = 1;
    lastGraphSelectionKey = "";
    loadingGraph = false;
  }

  onMount(() => {
    loadAppnameMapping();
    loadSavedBpmnDrawings();
  });
  onDestroy(stopVariantPlayback);

  $: if (idproject && bpmnDrawingsLoadedProject !== String(idproject))
    loadSavedBpmnDrawings();
  $: if (caseids.length && pendingBpmnDrawingReference) {
    const pending = pendingBpmnDrawingReference;
    pendingBpmnDrawingReference = null;
    loadBpmnDrawing(pending.drawing, pending.startnode);
  }

  // $: if (minFrequency > 1 && graphData?.edges) {
  //   graphEdges = graphData.edges.filter((edge) => edge.value >= minFrequency);
  //   graphNodes = graphData.nodes.filter((node) => {
  //     return graphEdges.some((edge) => edge.from === node.id || edge.to === node.id);
  //   });
  // }
</script>

<div class="task-Explorer-header-wrapper">
  <Header
    paddingRight={false}
    section="Object"
    area="Workflow Analysis"
    breadcrumb="Object Explorer"
  >
    <Filter allFiltersDisabled hideProcessFlow />
  </Header>
</div>
<SectionWrapper
  {fullscreen}
  fitScreen={true}
  emptyState={!caseIdsLoading && caseids.length === 0}
>
  <div class="main_wrapper" slot="main">
    {#if !showBpmnViewer}
      <div class="graph-left-toolbar">
        <OverflowMenu
          style="width:3rem; height:3rem;"
          menuOptionsClass="case-id-graph-menu"
        >
          <div
            slot="menu"
            class="canvas-menu-trigger"
            title={graphMode === "transitions" ? "Transitions" : "Variants"}
          >
            <img
              src={`/img/assistant/icons/${graphMode === "transitions" ? "transitions" : "variants"}.svg`}
              alt=""
            />
          </div>
          <OverflowMenuItem
            class={graphMode === "transitions" ? "active_color_option" : ""}
            text="Transitions"
            primaryFocus={graphMode === "transitions"}
            on:click={() => setGraphMode("transitions")}
          />
          <OverflowMenuItem
            class={graphMode === "variants" ? "active_color_option" : ""}
            text="Variants"
            primaryFocus={graphMode === "variants"}
            on:click={() => setGraphMode("variants")}
          />
        </OverflowMenu>
        <OverflowMenu
          style="width:3rem; height:3rem;"
          menuOptionsClass="case-id-graph-menu"
        >
          <div slot="menu">
            {#if edgeMetric === 0}
              <span
                class="canvas-menu-trigger count-toolbar-button"
                title="Count">#</span
              >
            {:else}
              <span class="canvas-menu-trigger" title="Avg. Throughput Time"
                ><img src="/img/assistant/icons/schedule.svg" alt="" /></span
              >
            {/if}
          </div>
          <OverflowMenuItem
            class={edgeMetric === 0 ? "active_color_option" : ""}
            text="Count"
            primaryFocus={edgeMetric === 0}
            on:click={() => (edgeMetric = 0)}
          />
          <OverflowMenuItem
            class={edgeMetric === 1 ? "active_color_option" : ""}
            text="Avg. Throughput Time"
            primaryFocus={edgeMetric === 1}
            on:click={() => (edgeMetric = 1)}
          />
        </OverflowMenu>
        <OverflowMenu
          style="width:3rem; height:3rem;"
          menuOptionsClass="case-id-graph-menu"
        >
          <div
            slot="menu"
            class="canvas-menu-trigger"
            title="Color highlighting"
          >
            <img
              src={`/img/assistant/icons/${colorMetric === "none" ? "layers_clear" : colorMetric === "text" ? "text_fields" : colorMetric === "copy" ? "content_paste" : colorMetric === "time" ? "schedule" : "apps_dark"}.svg`}
              alt=""
            />
          </div>
          <OverflowMenuItem
            class={colorMetric === "none" ? "active_color_option" : ""}
            text="None"
            primaryFocus={colorMetric === "none"}
            on:click={() => (colorMetric = "none")}
          />
          <OverflowMenuItem
            class={colorMetric === "text" ? "active_color_option" : ""}
            text="Text"
            primaryFocus={colorMetric === "text"}
            on:click={() => (colorMetric = "text")}
          />
          <OverflowMenuItem
            class={colorMetric === "copy" ? "active_color_option" : ""}
            text="Copy & Paste"
            primaryFocus={colorMetric === "copy"}
            on:click={() => (colorMetric = "copy")}
          />
          <OverflowMenuItem
            class={colorMetric === "time" ? "active_color_option" : ""}
            text="Time"
            primaryFocus={colorMetric === "time"}
            on:click={() => (colorMetric = "time")}
          />
          <OverflowMenuItem
            class={colorMetric === "appType" ? "active_color_option" : ""}
            text="App Type"
            primaryFocus={colorMetric === "appType"}
            on:click={() => (colorMetric = "appType")}
          />
        </OverflowMenu>
      </div>
      <div class="graph-toolbar">
        <div class="toolbar-group">
          <button
            type="button"
            class="toolbar-button"
            title="Actions"
            on:click={() => (actionMenuOpen = !actionMenuOpen)}
          >
            <img src="/img/assistant/icons/download.svg" alt="" />
          </button>
          {#if actionMenuOpen}
            <div class="toolbar-popover action-menu">
              <button
                type="button"
                on:click={() => {
                  actionMenuOpen = false;
                  graphPreview?.downloadPNG("object-explorer.png");
                }}>Image PNG</button
              >
              <button
                type="button"
                on:click={() => {
                  actionMenuOpen = false;
                  bpmnExportLevel = "CASE";
                  bpmnModalOpen = true;
                }}>BPMN 2.0 (Case)</button
              >
              <button
                type="button"
                on:click={() => {
                  actionMenuOpen = false;
                  bpmnExportLevel = "DRAW";
                  bpmnModalOpen = true;
                }}>BPMN 2.0 (Draw)</button
              >
            </div>
          {/if}
        </div>
        <button
          type="button"
          class="toolbar-button"
          class:active={paxbotOpen}
          title="Paxbot"
          on:click={() => (paxbotOpen = !paxbotOpen)}
        >
          <img src="/icons/bot.svg" alt="" />
        </button>
      </div>

      <div class="kpi-stack">
        <section class="kpi-card">
          <button
            type="button"
            class="legend-header"
            aria-expanded={quantificationOpen}
            on:click={() => (quantificationOpen = !quantificationOpen)}
          >
            <span>Quantification</span>
            <svg class="kpi-chevron" viewBox="0 0 17 10" aria-hidden="true"
              ><path
                d={quantificationOpen
                  ? "M8.5 0L17 8.5L16.11 9.39L8.5 1.77L0.89 9.38L0 8.49L8.5 0Z"
                  : "M8.5 9.39L0 0.89L0.89 0L8.5 7.62L16.11 0.01L17 0.9L8.5 9.39Z"}
                fill="currentColor"
              /></svg
            >
          </button>
          {#if quantificationOpen}
            <div class="quantification-grid">
              <div>
                <strong
                  >{graphNodes.length &&
                  selectionKpis.cases !== null &&
                  Number.isFinite(Number(selectionKpis.cases))
                    ? selectionKpis.cases
                    : "-"}</strong
                ><span>Cases</span>
              </div>
              <div>
                <strong
                  >{graphNodes.length
                    ? formatOptionalTime(selectionKpis.averageDurationSeconds)
                    : "-"}</strong
                ><span>Avg. Time</span>
              </div>
              <div>
                <strong
                  >{graphNodes.length &&
                  selectionKpis.coverage !== null &&
                  Number.isFinite(Number(selectionKpis.coverage))
                    ? `${Math.round(selectionKpis.coverage * 100)}%`
                    : "-"}</strong
                ><span>Coverage</span>
              </div>
            </div>
          {/if}
        </section>
        <section
          class="kpi-card applications-card"
          class:open={applicationsOpen}
        >
          <button
            type="button"
            class="legend-header"
            aria-expanded={applicationsOpen}
            on:click={() => (applicationsOpen = !applicationsOpen)}
          >
            <span>Applications</span>
            <svg class="kpi-chevron" viewBox="0 0 17 10" aria-hidden="true"
              ><path
                d={applicationsOpen
                  ? "M8.5 0L17 8.5L16.11 9.39L8.5 1.77L0.89 9.38L0 8.49L8.5 0Z"
                  : "M8.5 9.39L0 0.89L0.89 0L8.5 7.62L16.11 0.01L17 0.9L8.5 9.39Z"}
                fill="currentColor"
              /></svg
            >
          </button>
          {#if applicationsOpen}
            {#each selectionKpis.applications as application}
              <div class="application-row">
                <span title={application.name}>{application.name}</span>
                <i
                  ><b style:width={`${Math.round(application.share * 100)}%`}
                  ></b></i
                >
                <strong>{Math.round(application.share * 100)}%</strong>
              </div>
            {:else}
              <p>No application data</p>
            {/each}
          {/if}
        </section>
        {#if legendItems.length}
          <section class="kpi-card legend-card">
            <button
              type="button"
              class="legend-header"
              aria-expanded={legendOpen}
              on:click={() => (legendOpen = !legendOpen)}
            >
              <span>Legend</span>
              <svg class="kpi-chevron" viewBox="0 0 17 10" aria-hidden="true"
                ><path
                  d={legendOpen
                    ? "M8.5 0L17 8.5L16.11 9.39L8.5 1.77L0.89 9.38L0 8.49L8.5 0Z"
                    : "M8.5 9.39L0 0.89L0.89 0L8.5 7.62L16.11 0.01L17 0.9L8.5 9.39Z"}
                  fill="currentColor"
                /></svg
              >
            </button>
            {#if legendOpen}
              <div class="legend-list">
                {#each legendItems as item}
                  <div>
                    <span style:background-color={item.color}></span>
                    <p>{item.label}</p>
                  </div>
                {/each}
              </div>
            {/if}
          </section>
        {/if}
      </div>
    {/if}
    <div class="canvas">
      {#if showBpmnViewer}
        <BpmnViewer
          bind:this={bpmnViewerElement}
          bpmnXml={generatedBpmnXml}
          exportFormat={bpmnExportFormat}
          orientation={bpmnOrientation}
          {bicIdentifier}
          {bicValidTo}
          {bicChangeLog}
          {bicGenerateTexts}
          activityMetrics={generatedBpmnActivityMetrics}
          textLevel="process"
          saving={bpmnSaving}
          saved={bpmnSaved}
          on:save={saveBpmnToFramework}
          on:close={() => {
            showBpmnViewer = false;
            generatedBpmnXml = "";
            generatedBpmnFrameworkPath = [];
            generatedBpmnName = "";
            generatedBpmnActivityMetrics = [];
            selectedBpmnDrawingId = "";
            selectedBpmnDrawingStartnode = "";
            bpmnSaved = false;
          }}
        />
      {:else if loadingGraph}
        <div class="loading_wrapper">
          <Loading withOverlay={false} />
        </div>
      {:else if selectedCaseids.length === 0 && !activeBpmnDrawingGraph}
        <div class="no_process">Select Object</div>
      {:else if graphNodes.length === 0}
        <div class="no_process">No graph data available</div>
      {:else}
        {#key graphRevision}
          <CaseIdGraphPreview
            bind:this={graphPreview}
            nodesData={graphNodes}
            edgesData={graphEdges}
            {minFrequency}
            controlsBottom="4.75rem"
            {playbackNode}
            {playbackFrom}
            {playbackTo}
            {playbackDuration}
            bind:edgeMetric
            {colorMetric}
            {appTypesByName}
            showMetricSwitcher={false}
            preserveMissingValues={Boolean(activeBpmnDrawingGraph)}
          />
        {/key}
      {/if}
    </div>
    {#if paxbotOpen}
      <section
        class="paxbot-chat"
        aria-label="Paxbot Object Explorer Assistant"
      >
        <header>
          <div>
            <strong>Pax AI</strong><span>Object Explorer Assistant</span>
          </div>
          <div class="paxbot-header-actions">
            <select bind:value={paxbotLanguage} aria-label="Pax AI language">
              {#each paxbotLanguages as language}<option value={language.key}
                  >{language.label}</option
                >{/each}
            </select>
            <button
              type="button"
              class="paxbot-reset"
              aria-label="Start new chat"
              title="Start new chat"
              on:click={resetPaxbotChat}>+</button
            >
            <button
              type="button"
              class="paxbot-close"
              aria-label="Close Paxbot"
              on:click={() => (paxbotOpen = false)}
              ><img src="/img/assistant/icons/close.svg" alt="" /></button
            >
          </div>
        </header>
        <div class="paxbot-messages">
          {#each paxbotMessages as message}
            <div class:user={message.role === "user"} class="paxbot-message">
              {#if message.role === "assistant"}<img
                  src="/img/assistant/paxbot_chatsymbol.svg"
                  alt=""
                />{/if}
              <span>{message.role === "user" ? "You" : "Paxbot"}</span>
              <p>{message.text}</p>
            </div>
          {/each}
          {#if paxbotLoading}<div class="paxbot-message">
              <img src="/img/assistant/paxbot_chatsymbol.svg" alt="" /><span
                >Pax AI</span
              >
              <p>Thinking...</p>
            </div>{/if}
        </div>
        <div class="paxbot-chips">
          <button
            type="button"
            on:click={() => askPaxbot("Which variant occurs most often?")}
            >Most frequent variant</button
          >
          <button
            type="button"
            on:click={() => askPaxbot("Which transitions occur most often?")}
            >Most frequent transitions</button
          >
          <button
            type="button"
            on:click={() =>
              askPaxbot("Which applications dominate the selected cases?")}
            >Dominant applications</button
          >
          <button
            type="button"
            on:click={() =>
              askPaxbot("In which task activities were the objects found?")}
            >Object locations</button
          >
        </div>
        <form on:submit|preventDefault={() => askPaxbot()}>
          <textarea
            rows="1"
            bind:value={paxbotInput}
            placeholder="Ask Pax AI..."
          ></textarea>
          <button type="submit" disabled={!paxbotInput.trim() || paxbotLoading}
            ><img src="/img/assistant/icons/arrow_right.svg" alt="" /></button
          >
        </form>
        <small>Pax AI can make mistakes. Check important results.</small>
      </section>
      <img
        class="paxbot-mascot"
        src="/img/assistant/paxbot_portrait.png"
        alt="Paxbot"
      />
    {/if}
    <div class="menu_overlay bottom">
      <div class="button_wrapper">
        {#if graphMode === "variants"}
          <ButtonPlusMinus
            text="Variants"
            value={selectedVariantIds.length}
            defaultvalue={caseVariants.length}
            minValue={0}
            increment={() => {
              const variant = caseVariants.find(
                (item) => !selectedVariantIds.includes(item.id),
              );
              if (variant) toggleVariant(variant.id);
              return selectedVariantIds.length;
            }}
            decrement={() => {
              const id = selectedVariantIds[selectedVariantIds.length - 1];
              if (id) toggleVariant(id);
              return selectedVariantIds.length;
            }}
            reset={() =>
              selectVariants(caseVariants.map((variant) => variant.id))}
            fullDisable={loadingGraph || !caseVariants.length}
          />
        {:else}
          <ButtonPlusMinus
            text="Transitions"
            value={selectedTransitionIds.length}
            defaultvalue={rawGraphEdges.length}
            minValue={0}
            increment={addNextTransition}
            decrement={removeLastTransition}
            reset={selectAllTransitions}
            fullDisable={loadingGraph || !rawGraphEdges.length}
          />
          <ButtonPlusMinus
            text="Activities"
            value={selectedTransitionActivities.length}
            defaultvalue={graphNodes.filter((node) => node.parentNode).length}
            minValue={0}
            increment={addNextActivity}
            decrement={removeLastActivity}
            reset={selectAllTransitions}
            fullDisable={loadingGraph || !rawGraphEdges.length}
          />
        {/if}
        <ButtonPlusMinus
          text="Min Freq"
          bind:value={minFrequency}
          defaultvalue={1}
          minValue={1}
          increment={(value) => Number(value) + 1}
          decrement={(value) => Math.max(Number(value) - 1, 0)}
          fullDisable={loadingGraph || !graphEdges?.length}
        />
      </div>
      <div class="button_wrapper">
        <ButtonPlusMinus
          text="Zoom"
          bind:value={zoomLevel}
          showValue={false}
          reset={() => {
            showBpmnViewer
              ? bpmnViewerElement?.resetZoom()
              : graphPreview?.centerGraph();
            zoomLevel = 100;
          }}
          increment={(value) => {
            showBpmnViewer
              ? bpmnViewerElement?.zoomIn()
              : graphPreview?.incrementZoom();
            return value;
          }}
          decrement={(value) => {
            showBpmnViewer
              ? bpmnViewerElement?.zoomOut()
              : graphPreview?.decrementZoom();
            return value;
          }}
          fullDisable={loadingGraph || !graphNodes.length}
        />
        <ButtonIconOnly
          icon="fit_screen"
          title="Fit graph to view"
          on:click={() => {
            showBpmnViewer
              ? bpmnViewerElement?.resetZoom()
              : graphPreview?.centerGraph();
            zoomLevel = 100;
          }}
        />
        <ButtonIconOnly
          icon="fullscreen"
          title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
          on:click={toggleFullscreen}
        />
      </div>
    </div>
  </div>

  <div class="menu" slot="menu">
    <Accordion heading={"Object Selection"} open={true}>
      <CaseIdSelection
        {caseids}
        manualDrawings={manualDrawingOptions}
        bind:selectedCaseids
        bind:selectedStartnodes
        selectedManualDrawingId={selectedBpmnDrawingId}
        selectedManualStartnode={selectedBpmnDrawingStartnode}
        on:selectManual={(event) => {
          const drawing = savedBpmnDrawings.find(
            (item) => item.id === event.detail.id,
          );
          if (drawing) loadBpmnDrawing(drawing, event.detail.startnode);
        }}
        on:deleteManual={(event) => {
          const drawing = savedBpmnDrawings.find(
            (item) => item.id === event.detail.id,
          );
          if (drawing) openDeleteBpmnDrawing(drawing);
        }}
        on:renameManual={(event) =>
          renameBpmnDrawing(event.detail.id, event.detail.name)}
      />
    </Accordion>
    <Accordion open={true}>
      <span slot="heading" class="accordion-heading">
        {graphMode === "transitions" ? "Transitions" : "Variants"}
        <Tooltip
          text={graphMode === "transitions"
            ? "Select which activity connections are visible in the graph."
            : "Duration is measured from the first to the last event of each object. Time does not affect variant grouping."}
        />
      </span>
      <div class="variants-panel">
        {#if loadingGraph}
          <Loading withOverlay={false} small />
        {:else if graphMode === "transitions"}
          <div class="variant-selection-header">
            <strong
              >{selectedTransitionIds.length} / {transitionOptions.length} selected</strong
            >
            <div>
              <button type="button" on:click={selectAllTransitions}
                >Select all</button
              >
              <button type="button" on:click={clearTransitions}
                >Deselect all</button
              >
            </div>
          </div>
          <div class="variant-stats">
            <div>
              <span>Average (Total)</span><strong
                >{formatOptionalTime(
                  selectedVariantStats.averageDurationSeconds,
                )}</strong
              >
            </div>
            <div>
              <span>Median (Total)</span><strong
                >{formatOptionalTime(
                  selectedVariantStats.medianDurationSeconds,
                )}</strong
              >
            </div>
            <div>
              <span>Longest (Total)</span><strong
                >{formatOptionalTime(
                  selectedVariantStats.longestDurationSeconds,
                )}</strong
              >
            </div>
          </div>
          <div class="transition-header">
            <span>From</span><span>To</span><span>Count</span>
          </div>
          <div class="transition-list">
            {#each displayedTransitions as edge}
              <button
                type="button"
                class="transition-row"
                class:selected={selectedTransitionIds.includes(
                  transitionId(edge),
                )}
                on:click={() => toggleTransition(edge)}
              >
                <span title={edge.from}>{edge.from}</span>
                <span title={edge.to}>{edge.to}</span>
                <strong
                  >{edgeMetric === 0
                    ? edge.value !== null &&
                      edge.value !== undefined &&
                      edge.value !== "" &&
                      Number.isFinite(Number(edge.value))
                      ? edge.value
                      : "-"
                    : formatTransitionDuration(
                        edge.averageDurationSeconds,
                      )}</strong
                >
              </button>
            {/each}
          </div>
          {#if transitionOptions.length}
            <AccordionPagination
              total={transitionOptions.length}
              bind:currentpage={transitionPage}
              itemsPerPage={transitionsPerPage}
              disablePrev={transitionPage === 1}
              disableNext={transitionPage >= transitionPageCount}
            />
          {:else}
            <div class="empty-pagination">-</div>
          {/if}
        {:else if !caseVariants.length}
          <p class="empty-variants">No variants available</p>
        {:else}
          <div class="variant-selection-header">
            <strong
              >{selectedVariants.length} / {caseVariants.length} selected</strong
            >
            <div>
              <button
                type="button"
                disabled={updatingGraph}
                on:click={() =>
                  selectVariants(caseVariants.map((variant) => variant.id))}
                >Select all</button
              >
              <button
                type="button"
                disabled={updatingGraph}
                on:click={() => selectVariants([])}>Deselect all</button
              >
            </div>
          </div>
          <div class="variant-stats">
            <div
              title="Average time from the first to the last event of each selected object"
            >
              <span>Average (Total)</span><strong
                >{formatOptionalTime(
                  selectedVariantStats.averageDurationSeconds,
                )}</strong
              >
            </div>
            <div title="Median of the selected variant averages">
              <span>Median (Total)</span><strong
                >{formatOptionalTime(
                  selectedVariantStats.medianDurationSeconds,
                )}</strong
              >
            </div>
            <div title="Longest selected variant average">
              <span>Longest (Total)</span><strong
                >{formatOptionalTime(
                  selectedVariantStats.longestDurationSeconds,
                )}</strong
              >
            </div>
          </div>
          <div class="variant-list">
            {#each displayedVariants as variant}
              <div
                class="variant-row"
                class:selected={selectedVariantIds.includes(variant.id)}
                class:playing={playingVariantId === variant.id}
                title={(variant.path || []).join(" → ")}
              >
                <input
                  type="checkbox"
                  disabled={updatingGraph}
                  checked={selectedVariantIds.includes(variant.id)}
                  aria-label={`Show variant ${variant.name}`}
                  on:change={() => toggleVariant(variant.id)}
                />
                <div class="variant-copy">
                  <strong>Variant {variant.name}</strong>
                  <span
                    >{variant.count} cases · {Math.round(
                      variant.coverage * 100,
                    )}% · {formatOptionalTime(
                      variant.averageDurationSeconds,
                    )}</span
                  >
                </div>
                <button
                  type="button"
                  class="variant-play"
                  disabled={!selectedVariantIds.includes(variant.id)}
                  aria-label="Play variant"
                  title="Play variant"
                  on:click={() => playVariant(variant)}
                >
                  <span
                    class:stop={playingVariantId === variant.id}
                    class:play={playingVariantId !== variant.id}
                  ></span>
                </button>
              </div>
            {/each}
          </div>
          {#if caseVariants.length}
            <AccordionPagination
              total={caseVariants.length}
              bind:currentpage={variantPage}
              itemsPerPage={variantsPerPage}
              disablePrev={variantPage === 1}
              disableNext={variantPage >= variantPageCount}
            />
          {/if}
        {/if}
      </div>
    </Accordion>
  </div>
  <div slot="emptyState" class="empty_state_text">
    <div class="empty_state_title">
      No objects are available for this project.
    </div>
    <div class="empty_state_hint">
      Configure and track at least one object number range in Project Setup to
      create objects for Object Explorer.
    </div>
  </div>
</SectionWrapper>

<CaseIdBpmnExportModal
  open={bpmnModalOpen}
  level={bpmnExportLevel}
  {idproject}
  {graphMode}
  nodes={graphNodes}
  edges={graphEdges}
  variants={caseVariants}
  {selectedVariantIds}
  recordTypes={(bpmnExportLevel === "DRAW"
    ? caseids
    : caseids.filter((item) => selectedCaseids.includes(item.id))
  ).map((item) => ({
    name: item.record_type,
    ranges: item.ranges || [{ prefix: item.prefix, length: item.length }],
    startNodes: item.startnodes || [],
  }))}
  selectedStartNodes={selectedStartnodes}
  savedDrawings={savedBpmnDrawings}
  taskName={bpmnTaskName}
  on:close={() => (bpmnModalOpen = false)}
  on:save={saveBpmnDrawing}
  on:export={(event) => {
    generatedBpmnXml = event.detail.xml;
    generatedBpmnFrameworkPath = event.detail.frameworkPath || [];
    generatedBpmnName = event.detail.name || bpmnTaskName;
    generatedBpmnActivityMetrics = event.detail.activityMetrics || [];
    bpmnExportFormat = event.detail.exportFormat || "standard";
    bpmnOrientation = event.detail.orientation || "horizontal";
    bicIdentifier = event.detail.bicIdentifier || "";
    bicValidTo = event.detail.bicValidTo || "";
    bicChangeLog = event.detail.bicChangeLog || "";
    bicGenerateTexts = Boolean(event.detail.bicGenerateTexts);
    selectedBpmnDrawingId = "";
    selectedBpmnDrawingStartnode = "";
    bpmnSaved = false;
    bpmnModalOpen = false;
    showBpmnViewer = true;
  }}
/>

{#if bpmnSaveNotice}
  <div style="position:fixed; right:1rem; top:4rem; z-index:10000;">
    <ToastNotification
      kind={bpmnSaveNotice.kind}
      title={bpmnSaveNotice.title}
      subtitle={bpmnSaveNotice.message}
      timeout={bpmnSaveNotice.kind === "success" ? 4000 : null}
      on:close={() => (bpmnSaveNotice = null)}
    />
  </div>
{/if}

<DangerModal
  danger
  bind:open={deleteBpmnDrawingOpen}
  modalHeading="Delete BPMN Drawing"
  primaryButtonText="Delete drawing"
  secondaryButtonText="Cancel"
  preventCloseOnClickOutside
  on:click:button--primary={confirmDeleteBpmnDrawing}
  on:click:button--secondary={() => {
    deleteBpmnDrawingOpen = false;
    bpmnDrawingToDelete = null;
  }}
  on:close={() => {
    deleteBpmnDrawingOpen = false;
    bpmnDrawingToDelete = null;
  }}
>
  <p class="delete-bpmn-drawing">
    Are you sure you want to delete this BPMN drawing?{#if bpmnDrawingToDelete?.name}<strong
        >{bpmnDrawingToDelete.name}</strong
      >{/if}
  </p>
</DangerModal>

<style>
  .empty_state_title,
  .empty_state_hint {
    font-size: 1rem;
  }

  .empty_state_title {
    color: var(--pax-black);
  }

  .empty_state_hint {
    color: var(--pax-grey);
    margin-top: 0.5rem;
  }

  .main_wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .canvas {
    height: 100%;
    min-height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .loading_wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .no_process {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    font-size: 2rem;
    color: var(--pax-black);
  }

  .menu_overlay {
    position: absolute;
    width: 100%;
    z-index: 1; /* Stellt sicher, dass das Overlay über dem Canvas liegt */
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: transparent !important;
    background-color: transparent !important;
  }

  .bottom {
    bottom: 0;
  }

  .button_wrapper {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .menu {
    box-sizing: border-box;
    height: calc(100vh - 6rem);
    max-height: calc(100vh - 6rem);
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    overflow-x: hidden;
    overflow-y: auto;
    padding-right: 0.2rem;
  }

  .graph-toolbar {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    z-index: 10;
    display: flex;
    gap: 0.5rem;
  }

  .graph-left-toolbar {
    position: absolute;
    top: 0.25rem;
    left: 0.25rem;
    z-index: 10;
    display: flex;
    gap: 0.5rem;
  }

  .count-toolbar-button {
    font-size: 1.4rem;
    font-weight: 400;
  }

  .canvas-menu-trigger {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--pax-white);
    pointer-events: none;
  }

  .toolbar-group {
    position: relative;
  }

  .toolbar-button {
    width: 3rem;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 0.5rem;
    background: #1c1c20;
    cursor: pointer;
  }

  .toolbar-button img {
    width: 1.25rem;
    height: 1.25rem;
    filter: brightness(0) invert(1);
  }

  .toolbar-button.active {
    background: #ff5f1f;
  }

  .toolbar-button:hover {
    background: #2a2a2f;
  }

  .toolbar-popover {
    position: absolute;
    top: calc(100% + 0.4rem);
    right: 0;
    min-width: 12.5rem;
    padding: 0.35rem;
    border: 1px solid #d7dbe0;
    border-radius: 0.5rem;
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
  }

  .toolbar-popover button {
    width: 100%;
    padding: 0.55rem 0.65rem;
    border: 0;
    border-radius: 0.35rem;
    background: transparent;
    text-align: left;
    cursor: pointer;
  }

  .toolbar-popover button:hover {
    background: #f0f0f0;
    font-weight: 600;
  }

  .kpi-stack {
    position: absolute;
    top: 4rem;
    left: 0;
    z-index: 8;
    width: 16.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    pointer-events: auto;
  }

  .kpi-card {
    overflow: hidden;
    border: 1px solid #d7dbe0;
    border-radius: 0.55rem;
    background: rgba(255, 255, 255, 0.96);
  }

  .quantification-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0.8rem;
  }

  .quantification-grid div {
    min-width: 0;
    display: flex;
    align-items: center;
    flex-direction: column;
  }

  .quantification-grid strong {
    max-width: 100%;
    overflow: hidden;
    font-size: 1rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .quantification-grid span,
  .applications-card p {
    color: #6f7681;
    font-size: 0.68rem;
  }

  .applications-card {
    padding-bottom: 0;
  }

  .applications-card.open {
    padding-bottom: 0.75rem;
  }

  .application-row {
    display: grid;
    grid-template-columns: 5.2rem 1fr 2rem;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 1rem 0;
    font-size: 0.75rem;
  }

  .application-row > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .application-row i {
    height: 3px;
    background: #e1e4e8;
  }

  .application-row i b {
    height: 100%;
    display: block;
    background: #ff5f1f;
  }

  .applications-card p {
    margin: 0.75rem 1rem 0;
  }

  .legend-header {
    width: 100%;
    min-height: 3.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.85rem 1rem;
    border: 0;
    border-bottom: 1px solid #d7dbe0;
    background: transparent;
    cursor: pointer;
    font-size: 0.85rem;
  }

  .kpi-chevron {
    width: 1rem;
    height: 0.6rem;
    flex: 0 0 auto;
    color: var(--pax-black);
  }

  .legend-list {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    padding: 1rem;
    border-top: 1px solid #d7dbe0;
  }

  .legend-list div {
    display: grid;
    grid-template-columns: 1.1rem 1fr;
    align-items: center;
    gap: 0.65rem;
    font-size: 0.78rem;
  }

  .legend-list div span {
    width: 1.1rem;
    height: 1.1rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.25rem;
  }

  .legend-list p {
    margin: 0;
  }

  .variants-panel {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .variant-selection-header {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    font-size: 0.75rem;
  }

  .accordion-heading {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  .variant-selection-header > div {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.4rem;
  }

  .variant-selection-header button {
    padding: 0.4rem;
    border: 0;
    border-radius: 0.35rem;
    background: #1c1c20;
    color: #fff;
    cursor: pointer;
  }

  .variant-selection-header button:disabled {
    opacity: 0.55;
    cursor: default;
  }

  .variant-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }

  .variant-stats div {
    min-width: 0;
    padding: 0.55rem;
    border: 1px solid #d7dbe0;
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .variant-stats span,
  .variant-copy span {
    color: #6f7681;
    font-size: 0.7rem;
  }

  .variant-stats strong {
    overflow: hidden;
    font-size: 0.75rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .variant-list {
    min-width: 0;
    width: 100%;
    max-height: 21rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .transition-header,
  .transition-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.5rem;
  }

  .transition-header {
    color: #6f7681;
    font-size: 0.72rem;
  }

  .transition-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .transition-row {
    height: 2.125rem;
    padding: 0 0.75rem;
    border: 1px solid var(--pax-smoke);
    border-radius: 0.35rem;
    background: var(--pax-smoke);
    font-size: 0.75rem;
    cursor: pointer;
    text-align: left;
  }

  .transition-row.selected {
    border-color: var(--pax-black);
  }

  .transition-row span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .transition-row strong {
    text-align: right;
  }

  .paxbot-chat {
    position: absolute;
    top: 4.75rem;
    right: 1rem;
    z-index: 20;
    width: min(23rem, calc(100% - 2rem));
    max-height: calc(100% - 10rem);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #d7dbe0;
    border-radius: 0.65rem;
    background: #fff;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  .paxbot-chat header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.8rem 1rem;
    background: #ff5f1f;
    color: #fff;
  }

  .paxbot-chat header div {
    display: flex;
    flex-direction: column;
  }

  .paxbot-chat header span,
  .paxbot-chat small {
    font-size: 0.68rem;
  }

  .paxbot-chat header button {
    border: 0;
    background: transparent;
    color: #fff;
    cursor: pointer;
    font-size: 1.4rem;
  }

  .paxbot-messages {
    min-height: 9rem;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    overflow-y: auto;
    padding: 0.8rem;
  }

  .paxbot-message {
    max-width: 88%;
    padding: 0.55rem 0.7rem;
    border-radius: 0.55rem;
    background: #f2f2f2;
  }

  .paxbot-message.user {
    align-self: flex-end;
    background: #1c1c20;
    color: #fff;
  }

  .paxbot-message span {
    font-size: 0.65rem;
    font-weight: 700;
  }

  .paxbot-message p {
    margin: 0.2rem 0 0;
    white-space: pre-wrap;
    font-size: 0.78rem;
  }

  .paxbot-chips {
    display: flex;
    gap: 0.35rem;
    overflow-x: auto;
    padding: 0 0.8rem 0.6rem;
  }

  .paxbot-chips button {
    flex: 0 0 auto;
    padding: 0.35rem 0.5rem;
    border: 1px solid #d7dbe0;
    border-radius: 999px;
    background: #fff;
    cursor: pointer;
    font-size: 0.68rem;
  }

  .paxbot-chat form {
    display: grid;
    grid-template-columns: 1fr 2.4rem;
    gap: 0.4rem;
    padding: 0.65rem 0.8rem;
    border-top: 1px solid #d7dbe0;
  }

  .paxbot-chat textarea {
    resize: none;
    padding: 0.5rem;
    border: 1px solid #d7dbe0;
    border-radius: 0.4rem;
  }

  .paxbot-chat form button {
    border: 0;
    border-radius: 0.4rem;
    background: #1c1c20;
    color: #fff;
    cursor: pointer;
  }

  .paxbot-chat small {
    padding: 0 0.85rem 0.55rem;
    border-radius: 0 0 0.5rem 0.5rem;
    background: #fff;
    color: #8a929b;
    line-height: 1.25;
    text-align: center;
  }

  .variant-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.55rem 0.65rem;
    border: 1px solid #d7dbe0;
    border-radius: 0.5rem;
    background: #fff;
  }

  .variant-row.playing {
    border-color: #ff5f1f;
  }

  .variant-row:not(.selected) {
    opacity: 0.62;
  }

  .variant-row input {
    width: 1rem !important;
    min-width: 1rem;
    height: 1rem;
    flex: 0 0 1rem;
    margin: 0;
    accent-color: #ff5f1f;
  }

  .variant-copy {
    min-width: 0;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.2rem;
  }

  .variant-copy strong,
  .variant-copy span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .variant-play {
    width: 1.8rem;
    height: 1.8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1.8rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: #f4f4f4;
    cursor: pointer;
  }

  .variant-play:hover {
    background: #e8e8e8;
  }

  .variant-play:disabled {
    cursor: default;
    opacity: 0.45;
  }

  .variant-play .play {
    width: 0;
    height: 0;
    margin-left: 2px;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 8px solid #1c1c20;
  }

  .variant-play .stop {
    width: 8px;
    height: 8px;
    background: #1c1c20;
  }

  .empty-variants {
    margin: 0;
    color: #6f7681;
    font-size: 0.8rem;
  }

  .empty-pagination {
    min-height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background: #f4f4f4;
    font-weight: 700;
  }

  .graph-left-toolbar :global(.bx--overflow-menu) {
    width: 3rem;
    height: 3rem;
    border-radius: 0.5rem;
    background-color: var(--pax-black);
  }

  .graph-left-toolbar :global(.bx--overflow-menu:hover) {
    background-color: #2a2a2f;
  }

  .graph-left-toolbar :global(.bx--overflow-menu img) {
    width: 1.25rem;
    height: 1.25rem;
    filter: brightness(0) invert(1);
  }

  :global(
      .case-id-graph-menu .active_color_option > .bx--overflow-menu-options__btn
    ) {
    background-color: #ffe8dd !important;
    border-left: 0.25rem solid var(--pax-orange) !important;
    font-weight: 600 !important;
  }

  .paxbot-chat {
    top: auto;
    right: 1rem;
    bottom: 5.85rem;
    z-index: 20;
    width: min(28rem, calc(100% - 2rem));
    height: min(37rem, calc(100% - 8rem));
    min-height: 18rem;
    max-height: none;
    overflow: visible;
    border-radius: 0.5rem;
    border: 1px solid #e1e4e8;
    box-shadow: 0 1rem 2.5rem rgba(24, 28, 34, 0.18);
  }

  .paxbot-chat header {
    gap: 1rem;
    padding: 0.9rem 1rem;
    background: var(--pax-orange);
  }

  .paxbot-chat header > div:first-child {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .paxbot-header-actions {
    display: flex !important;
    flex-direction: row !important;
    align-items: center;
    gap: 0.35rem;
  }

  .paxbot-header-actions select,
  .paxbot-reset,
  .paxbot-close {
    height: 1.75rem;
    border: 1px solid rgba(255, 255, 255, 0.55) !important;
    border-radius: 0.35rem;
    background: rgba(255, 255, 255, 0.14) !important;
    color: #fff;
  }

  .paxbot-header-actions select {
    padding: 0 0.35rem;
    font-size: 0.72rem;
    font-weight: 800;
  }

  .paxbot-header-actions select option {
    color: var(--pax-black);
  }

  .paxbot-reset,
  .paxbot-close {
    width: 1.75rem;
    flex: 0 0 1.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .paxbot-close {
    border-color: transparent !important;
    background: transparent !important;
  }

  .paxbot-close img {
    width: 0.75rem;
    height: 0.75rem;
    filter: brightness(0) invert(1);
  }

  .paxbot-messages {
    flex: 1 1 auto;
    min-height: 0;
    gap: 0.75rem;
    padding: 1rem;
    background: #f7f8fa;
  }

  .paxbot-message {
    max-width: 82%;
    display: grid;
    grid-template-columns: 1rem minmax(0, 1fr);
    column-gap: 0.5rem;
    padding: 0;
    background: transparent;
  }

  .paxbot-message > img {
    grid-row: 1 / 3;
    width: 1rem;
    height: 1rem;
    margin-top: 0.35rem;
  }

  .paxbot-message > span,
  .paxbot-message > p {
    grid-column: 2;
    padding-right: 0.8rem;
    padding-left: 0.8rem;
    background: #fff;
    border-right: 1px solid #e4e6ea;
    border-left: 1px solid #e4e6ea;
  }

  .paxbot-message > span {
    padding-top: 0.7rem;
    border-top: 1px solid #e4e6ea;
    border-radius: 0.5rem 0.5rem 0 0;
  }

  .paxbot-message > p {
    margin: 0;
    padding-top: 0.25rem;
    padding-bottom: 0.7rem;
    border-bottom: 1px solid #e4e6ea;
    border-radius: 0 0 0.5rem 0.5rem;
  }

  .paxbot-message.user {
    align-self: flex-end;
    display: block;
    padding: 0.7rem 0.8rem;
    border-radius: 0.5rem;
    background: var(--pax-black);
  }

  .paxbot-message.user > span,
  .paxbot-message.user > p {
    display: block;
    padding: 0;
    border: 0;
    background: transparent;
    color: #fff;
  }

  .paxbot-chips {
    flex-wrap: wrap;
    overflow: hidden;
    padding: 0.5rem 0.75rem 0.85rem;
    border-top: 1px solid #eef0f2;
  }

  .paxbot-chips button {
    flex: 1 1 calc(50% - 0.25rem);
    height: 1.75rem;
    overflow: hidden;
    padding: 0 0.65rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 700;
  }

  .paxbot-chat form {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    grid-template-columns: 1fr 2.5rem;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem 0.5rem;
    border-top: 0;
  }

  .paxbot-chat textarea,
  .paxbot-chat form button {
    min-height: 2.5rem;
  }

  .paxbot-chat textarea {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    max-height: 7rem;
    padding: 0.65rem 0.75rem;
    resize: vertical;
    font: inherit;
    font-size: 0.82rem;
    outline: none;
  }

  .paxbot-chat textarea:focus {
    border-color: var(--pax-orange);
  }

  .paxbot-chat form button {
    box-sizing: border-box;
    width: 2.5rem;
    height: 2.5rem;
    min-width: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-radius: 0.45rem;
  }

  .paxbot-chat form button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  .paxbot-chat form button:not(:disabled):hover {
    background: #2a2a2f;
  }

  .paxbot-chat form button img {
    width: 1rem;
    height: 1rem;
    filter: brightness(0) invert(1);
  }

  .paxbot-mascot {
    position: absolute;
    right: 1.55rem;
    bottom: -0.65rem;
    z-index: 21;
    height: 5.6rem;
    width: auto;
    pointer-events: none;
  }

  .delete-bpmn-drawing {
    display: grid;
    gap: 0.75rem;
  }
  .delete-bpmn-drawing strong {
    color: var(--pax-black);
  }
</style>
